import React from 'react';
import TableWidget, { HeaderMappingItem } from '../../../helpers/tableBuilder';
import { IFilterState } from '../../../contexts/FilterContext';
import { fetchAdServerData } from '../../../helpers/adserverData';

const detailsHeaderMapping: Record<string, HeaderMappingItem> = {
  date: {
    name: 'Datum',
    type: 'date',
    digits: 0,
    attach: '',
  },
  fit_dois: {
    name: 'DOIs',
    type: 'number',
    digits: 0,
    attach: '',
  },
  impressions: {
    name: 'Impressions',
    type: 'number',
    digits: 0,
    attach: '',
  },
  clicks: {
    name: 'Klicks',
    type: 'number',
    digits: 0,
    attach: '',
  },
  leads: {
    name: 'Leads',
    type: 'number',
    digits: 0,
    attach: '',
  },
  turnover: {
    name: 'Umsatz',
    type: 'number',
    digits: 0,
    attach: '€',
  },
  roi_30: {
    name: 'ROI30',
    type: 'percentage',
    digits: 2,
    attach: '%',
  },
  rpm: {
    name: 'RPM (Impressions)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  rpmc: {
    name: 'RPMC (Klicks)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
};

type ApiResponse = {
  date: string;
  fit_dois: number;
  impressions: number;
  clicks: number;
  leads: number;
  sum_anfo_30d: number;
};

type MappedData = {
  date: string;
  fit_dois: number;
  impressions: number;
  clicks: number;
  leads: number;
  turnover: number;
  sum_anfo_30d: number;
  roi_30: number;
  rpm: number;
  rpmc: number;
};

const getData: (filterState: IFilterState) => Promise<MappedData[]> = async (
  filterState: IFilterState,
) => {
  const data = await fetchAdServerData('/api/adserver/welcome-flows/doi-zone', filterState);
  return data.map((item: ApiResponse) => {
    const turnover = Number(item.leads) * Number(filterState.leadPrice);
    return {
      date: item.date,
      fit_dois: Number(item.fit_dois),
      impressions: Number(item.impressions),
      clicks: Number(item.clicks),
      leads: Number(item.leads),
      turnover: Number(turnover),
      sum_anfo_30d: Number(item.sum_anfo_30d),
      roi_30: item.leads ? Number(item.sum_anfo_30d) / turnover : 0,
      rpm: item.impressions ? (turnover / Number(item.impressions)) * 1000 : 0,
      rpmc: item.clicks ? (turnover / Number(item.clicks)) * 1000 : 0,
    };
  });
};

const computeSummary = (data: MappedData[]): MappedData => {
  const summaryData: MappedData = {
    date: 'Insgesamt',
    fit_dois: 0,
    impressions: 0,
    clicks: 0,
    leads: 0,
    turnover: 0,
    sum_anfo_30d: 0,
    roi_30: 0,
    rpm: 0,
    rpmc: 0,
  };
  let turnoverForImpressions = 0;
  data.forEach((item) => {
    summaryData.fit_dois += item.fit_dois;
    summaryData.impressions += item.impressions;
    summaryData.clicks += item.clicks;
    summaryData.leads += item.leads;
    summaryData.turnover += item.turnover;
    summaryData.sum_anfo_30d += item.sum_anfo_30d;
    // for rpm use only turnover, starting 2024-03-02 as this was first day to track impressions
    if (item.date >= '2024-03-02') {
      turnoverForImpressions += item.turnover;
    }
  });
  // Recalculate derived metrics for the summary
  summaryData.roi_30 = summaryData.leads > 0 ? summaryData.sum_anfo_30d / summaryData.turnover : 0;
  summaryData.rpm =
    summaryData.impressions > 0 ? (turnoverForImpressions / summaryData.impressions) * 1000 : 0;
  summaryData.rpmc =
    summaryData.clicks > 0 ? (summaryData.turnover / summaryData.clicks) * 1000 : 0;
  return summaryData;
};

const DoiZoneEvaluation: React.FC = () => {
  return (
    <>
      <TableWidget<MappedData>
        getData={(filterState) => getData(filterState)}
        computeSummary={computeSummary}
        detailsHeaderMapping={detailsHeaderMapping}
        initialSortColumn="date"
        excelDownloadName="doi-zone-evaluation"
        sortOrder="desc"
      />
    </>
  );
};

export default DoiZoneEvaluation;
